<template>
  <v-layout class="d-flex flex-column h-screen">
    <v-main :class="containerState" class="d-flex flex-grow-1">
      <v-container id="main-container-formula" fluid>
        <v-card class="mx-auto" flat>
          <v-card-text class="pa-0">
            <v-overlay
              v-model="overlay"
              class="align-center justify-center"
              contained
              opacity="1"
              scrim="white"
            >
              <v-progress-circular
                indeterminate
                size="64"
                color="secondary"
              ></v-progress-circular>
            </v-overlay>
          </v-card-text>
          <v-card-text class="pa-0 mx-auto d-flex justify-center">
            <v-sheet max-width="1280" width="100%">
              <v-row class="flex-nowrap">
                <v-col cols="6" class="mx-1 flex-grow-0 flex-shrink-1">
                  <FormulaItems
                    :containerHeight="containerHeight"
                    :selectedItems="sessionItem.definition.selected"
                  ></FormulaItems>
                </v-col>
                <v-col cols="6" class="mx-1 flex-grow-0 flex-shrink-1">
                  <v-card
                    id="formula-card"
                    elevation="1"
                    rounded
                    :max-height="`calc(${this.containerHeight} - 52px)`"
                  >
                    <v-toolbar color="secondary">
                      <v-toolbar-title class="text-white">
                        {{ $t("labels.kpi-calculation-formula") }}
                        <div class="text-caption">
                          {{ $t("strings.selected-items-hint") }}
                        </div>
                      </v-toolbar-title>
                      <template v-slot:append>
                        <v-tooltip location="left">
                          <template v-slot:activator="{ props }">
                            <v-icon
                              class="mx-4"
                              size="large"
                              v-bind="props"
                              icon="mdi-information-outline"
                            ></v-icon>
                          </template>
                          <p class="mt-2">
                            {{ $t("strings.formula-hint") }}
                          </p>
                          <div class="mb-2">
                            {{ $t("strings.formula-hint-example") }}
                          </div>
                          <div class="text-center">
                            <FormulaHint
                              :measuresArray="
                                this.sessionItem.definition.selected
                              "
                            ></FormulaHint>
                          </div>
                        </v-tooltip>
                      </template>
                    </v-toolbar>
                    <v-card-text class="my-0">
                      <!-- Formula -->
                      <v-card flat>
                        <v-card-item>
                          <template v-slot:prepend>
                            <v-avatar color="secondary" size="small">
                              <v-icon icon="mdi-function"></v-icon>
                            </v-avatar>
                          </template>
                          <v-card-title class="text-subtitle-1 text-primary">
                            {{ $t("labels.title-formula") }}
                          </v-card-title>
                          <v-card-subtitle class="text-caption">
                            {{ $t("strings.formula-description") }}
                            <v-tooltip activator="parent" location="top">
                              {{ $t("strings.formula-description") }}
                            </v-tooltip>
                          </v-card-subtitle>
                        </v-card-item>
                        <v-card-text>
                          <MathliveField
                            :valueFormula="formula"
                            :locale="locale"
                            @writeFormula="writeFormula"
                          />
                        </v-card-text>
                        <v-card-text class="py-0">
                          <span class="text-caption text-primary">
                            <span
                              v-if="!formulaCheck().style"
                              class="font-weight-bold"
                            >
                              LaTeX:
                              <span class="text-body-2 font-weight-light">{{
                                formulaCheck().value
                              }}</span>
                            </span>
                            <span
                              v-else
                              class="font-weight-regular"
                              :class="formulaCheck().style"
                            >
                              {{ formulaCheck().value }}
                            </span>
                          </span>
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <span class="text-caption px-2">
                            <span v-if="enableContinue" class="text-green">
                              {{ $t("strings.validation-ok") }}
                            </span>
                            <span
                              v-else-if="formula.length > 0"
                              class="text-red"
                            >
                              {{ $t("strings.exec-validation") }}
                            </span>
                          </span>
                          <!-- Formula Validation Dialog -->
                          <DialogValidateFormula
                            :formula="formula"
                            :loading="loading"
                            :resultPreview="resultPreview"
                            @getPreview="getPreview"
                          />
                        </v-card-actions>
                      </v-card>
                      <v-divider></v-divider>
                      <!-- Scheduling -->
                      <v-card flat>
                        <v-card-item>
                          <template v-slot:prepend>
                            <v-avatar color="secondary" size="small">
                              <v-icon
                                icon="mdi-clock-time-five-outline"
                              ></v-icon>
                            </v-avatar>
                          </template>
                          <v-card-title class="text-subtitle-1 text-primary">
                            {{ $t("labels.title-scheduling") }}
                          </v-card-title>
                          <v-card-subtitle class="text-caption">
                            {{ $t("strings.scheduling-hint") }}
                            <v-tooltip activator="parent" location="top">
                              {{ $t("strings.scheduling-hint") }}
                            </v-tooltip>
                          </v-card-subtitle>
                        </v-card-item>
                        <v-card-text class="pt-2">
                          <v-row>
                            <v-col cols="5">
                              <v-text-field
                                class="custom-number-input"
                                v-model="sessionItem.formula.job.schedule.value"
                                :label="$t('labels.duration')"
                                variant="outlined"
                                density="compact"
                                name="scheduleValue"
                                maxlength="10"
                                type="number"
                                min="1"
                                :rules="[(v) => v > 0 || $t('labels.minimum')]"
                                :update:modelValue="
                                  this.schedulePositiveNumber(
                                    sessionItem.formula.job.schedule.value
                                  )
                                "
                                hide-details="auto"
                              >
                              </v-text-field>
                            </v-col>
                            <v-col cols="7">
                              <v-select
                                v-model="sessionItem.formula.job.schedule.time"
                                variant="outlined"
                                :items="periods"
                                item-title="text"
                                item-value="value"
                                density="compact"
                                :label="$t('labels.period')"
                                name="time"
                                :rules="[(v) => !!v || $t('labels.required')]"
                                hide-details="auto"
                              ></v-select>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                      <v-divider></v-divider>
                      <KpiTargetDefinition
                        :symbolsUnitsMeasures="kpimEditorConfig.unitSymbols"
                        :parentTargetValue="sessionItem.definition.targetValue"
                        :parentUnitMeasure="sessionItem.definition.unitMeasure"
                        @writeTargetValue="writeTargetValue"
                        @writeUnitMeasure="writeUnitMeasure"
                      ></KpiTargetDefinition>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-sheet>
          </v-card-text>
          <v-card-actions class="pb-0" :class="!overlay ? 'd-flex' : 'd-none'">
            <v-btn variant="outlined" color="secondary" @click="routeToHome">
              {{ $t("labels.cancel") }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              variant="elevated"
              color="secondary"
              @click.stop="routeToDefinition()"
            >
              {{ $t("labels.back") }}
            </v-btn>
            <v-btn
              :disabled="isButtonDisabled"
              variant="elevated"
              color="secondary"
              class="ml-2 text-white"
              @click.stop="saveAndContinue()"
            >
              <span v-if="sessionItem.id">{{ $t("labels.update") }}</span>
              <span v-else>{{ $t("labels.continue") }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-container>
    </v-main>
  </v-layout>
</template>

<script>
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";
import app from "./mixins/app.js";
import version from "../version";
import FormulaItems from "./components/FormulaItems.vue";
import MathliveField from "./components/MathliveField.vue";
import FormulaHint from "./components/FormulaHint.vue";
import DialogValidateFormula from "./components/DialogValidateFormula.vue";
import KpiTargetDefinition from "./components/KpiTargetDefinition.vue";
export default {
  name: "demf-kpim-editor-formula",
  components: {
    FormulaItems,
    MathliveField,
    KpiTargetDefinition,
    FormulaHint,
    DialogValidateFormula,
  },
  mixins: [events, utils, app],
  props: ["kpimEditorConfig", "realm"],
  data: function () {
    return {
      overlay: true,
      appVersion: version,
      containerState: "expanded",
      formula: "",
      isFormulaValid: false,
      measures: [],
      KPIs: [],
      containerHeight: "0px",
      resultPreview: "",
      loading: false,
      sessionItem: {
        definition: {
          selected: [],
          unitMeasure: "",
          targetValue: "",
        },
        formula: {
          expression: "",
          details: [],
          job: {
            schedule: {
              time: "hour",
              value: 1,
            },
            status: "PENDING",
          },
        },
      },
      locale: localStorage.getItem("lang") || "en",
    };
  },
  mounted() {
    console.log("Mounted demf-kpi-editor-formula v", this.appVersion);

    document.addEventListener(
      "language-localstorage-changed",
      this.handleLanguageChange
    );
    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener(
      "sidenav-localstorage-changed",
      this.handleSidenavChange
    );
    this.sessionItem = Object.assign(
      {},
      this.sessionItem,
      JSON.parse(sessionStorage.getItem("kpi"))
    );

    if (this.sessionItem.formula.expression.length !== 0) {
      this.formula = this.replaceValues(this.sessionItem.formula.expression);
    }

    this.categorizeSelectedItems();

    this.updateContainerHeight(); // Update dimensions
    window.addEventListener("resize", this.updateContainerHeight);
    this.overlay = false;
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateContainerHeight);
    document.removeEventListener(
      "language-localstorage-changed",
      this.handleLanguageChange
    );
    document.removeEventListener(
      "sidenav-localstorage-changed",
      this.handleSidenavChange
    );
  },
  computed: {
    itemsVirtualHeight() {
      return `calc(${this.containerHeight} - 64px - 52px)`;
    },
    periods() {
      return this.getPeriods();
    },
    isButtonDisabled() {
      return (
        !this.enableContinue ||
        this.formula.length === 0 ||
        !this.isFormulaValid ||
        !this.sessionItem.definition.targetValue ||
        !this.sessionItem.definition.unitMeasure
      );
    },
  },
  methods: {
    handleLanguageChange(event) {
      this.setLocale(event.detail.storage);
      this.locale = event.detail.storage;
    },
    handleSidenavChange(event) {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    },
    getPeriods() {
      const periods = [
        "minute",
        "hour",
        "day",
        "week",
        "month",
        "quarter",
        "year",
      ];
      return periods.map((period) => ({
        value: period,
        text: this.$t(
          `labels.${period}`,
          this.sessionItem.formula.job.schedule.value
        ),
      }));
    },
    //Calculate tag container height
    updateContainerHeight() {
      const element = document.getElementById("main-container-formula");
      if (element) {
        const elementCss = window.getComputedStyle(element);
        const paddingTop = parseFloat(
          elementCss.getPropertyValue("padding-top")
        );
        const paddingBottom = parseFloat(
          elementCss.getPropertyValue("padding-bottom")
        );
        this.containerHeight =
          element.offsetHeight - paddingTop - paddingBottom + "px";
      } else {
        console.warn("Not Found: ", element);
      }
    },

    formulaCheck() {
      let hint = {
        style: "",
        value: "",
      };

      if (this.formula.length === 0) {
        hint.style = "bg-info pa-1 rounded";
        hint.value = this.$t("strings.enter-formula");
        this.isFormulaValid = false;
        return hint;
      }

      const trimmedFormula = this.formula.trim().replace(/\s/g, "");
      const hasSelectedItem = this.sessionItem.definition.selected.some(
        (item) => trimmedFormula.includes(item.name.trim().replace(/\s/g, ""))
      );

      if (!hasSelectedItem) {
        hint.style = "bg-error pa-1 rounded";
        hint.value = this.$t("strings.no-measure-formula");
        this.isFormulaValid = false;
        return hint;
      }

      const invalidPatterns = [
        "()",
        "[]",
        "{}",
        "\\left\\lbrace\\right\\rbrace",
        "\\placeholder",
        "\\left(\\right)",
        "\\left\\lbrack\\right\\rbrack",
        "-+",
        "--",
        "++",
        "\\cdot\\cdot",
        /(\+|-|\*|\/|\\cdot)\s*\\right\)/,
        /^\\cdot/,
        /-$|\\cdot$|\+$/,
      ];

      const hasInvalidPattern = invalidPatterns.some((pattern) =>
        typeof pattern === "string"
          ? this.formula.includes(pattern)
          : pattern.test(this.formula)
      );

      if (
        hasInvalidPattern ||
        !this.checkBalancedDelimiters(this.formula) ||
        this.formula.includes("\\left(\\cdot") ||
        this.formula.includes("\\left\\lbrace\\cdot") ||
        this.formula.includes("\\left\\lbrack\\cdot")
      ) {
        hint.style = "bg-error pa-1 rounded";
        hint.value = this.$t("strings.invalid-formula");
        this.isFormulaValid = false;
        return hint;
      }

      hint.style = "";
      hint.value = this.replaceValues(this.formula);
      this.isFormulaValid = true;
      return hint;
    },

    //Adds the selected items to the appropriate array based on its type.
    categorizeSelectedItems() {
      this.sessionItem.definition.selected.forEach((item) => {
        if (item.type === "measure") {
          this.measures.push(item);
        } else {
          this.KPIs.push(item);
        }
      });
    },
    saveAndContinue() {
      this.grabUsedMeasures();
      this.sessionItem.formula.expression = this.formula;
      this.routeToDashboard();
    },
    writeFormula(val) {
      if (this.formula !== val) {
        this.formula = val; // Only update if the value has actually changed
      }
    },
    writeTargetValue(targetValue) {
      this.sessionItem.definition.targetValue = targetValue;
    },
    writeUnitMeasure(unitMeasure) {
      this.sessionItem.definition.unitMeasure = unitMeasure;
    },
  },
  watch: {
    formula(val) {
      this.validateFormula(val);
    },
  },
};
</script>
<style scoped>
main.expanded {
  margin-left: 56px;
}
main.reduced {
  margin-left: 256px;
}
main #main-container-formula {
  /*ControlPanel total height - toolbar - stepper - Footer*/
  height: calc(100vh - 64px - 104px - 40px);
  overflow-y: hidden;
}
#formula-card {
  overflow-y: auto;
}
</style>
